import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';

import { useBesluitId, usePatchBesluitId } from '@/api/queries/besluit';
import { useOpenModal } from '@/components/Modal';
import { SaveFailed } from '@/modals/SaveFailed';
import { ErrorMessage } from '@/utils/errorMessage';

import { ContentTemplate } from './-components/ContentTemplate/ContentTemplate';
import { AUTOSAVE_MUTATION_KEY } from './-components/Navigation/Navigation';
import { ToolbarContentPage } from './-components/ToolbarContentPage/ToolbarContentPage';

export const Route = createFileRoute(
  '/$bedrijfLineageId/editor/$besluitId/besluittekst'
)({
  loader: ({ params: { besluitId } }) =>
    useBesluitId.ensureQueryData(besluitId),
  component: function Besluittekst() {
    const { besluitId } = Route.useParams();

    const openModal = useOpenModal();

    const besluit = useBesluitId(besluitId);
    const patchBesluit = usePatchBesluitId({
      mutationKey: AUTOSAVE_MUTATION_KEY,
    });

    const onSave = useCallback(
      (value: string) => {
        if (besluit.data?.Besluit_Tekst === value) return;

        patchBesluit.mutate(
          {
            id: besluitId,
            data: {
              Besluit_Tekst: value,
            },
          },
          {
            onError: (error) => {
              const errorMessage = ErrorMessage.getLocalErrorMessage(error);

              if (errorMessage) {
                openModal(SaveFailed, {
                  tryAgain: () =>
                    patchBesluit.mutateAsync({
                      id: besluitId,
                      data: {
                        Besluit_Tekst: value,
                      },
                    }),
                });
              }
            },
          }
        );
      },
      [patchBesluit, besluitId, openModal, besluit.data?.Besluit_Tekst]
    );

    if (!besluit.data) {
      return null;
    }

    return (
      <>
        <ContentTemplate
          id="page-besluittekst-editor"
          title="Besluittekst"
          defaultValue={besluit.data.Besluit_Tekst ?? ''}
          onSave={onSave}
        />

        <ToolbarContentPage besluit={besluit.data} />
      </>
    );
  },
});
